import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Edit, Plus, Trash2, Phone, Mail, MapPin, Building, 
  Calendar, FileText, Users, Euro, Folder, Paperclip, ChevronDown, ChevronUp,
  User, Search, Shield, Key, Send, Loader2, CheckSquare, Clock,
  AlertTriangle, Landmark, Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { contactosAPI, expedientesAPI, facturasAPI, eventosAPI } from '../api';
import { toast } from '../hooks/use-toast';
import AdjuntosSection from '../components/AdjuntosSection';

// Subtipos organizados por tipo de entidad (igual que en ContactosNew)
const SUBTIPOS_POR_ENTIDAD = {
  persona: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'abogado_propio', nombre: 'Abogado Propio' },
    { codigo: 'abogado_contrario', nombre: 'Abogado Contrario' },
    { codigo: 'abogado_colaborador', nombre: 'Abogado Colaborador' },
    { codigo: 'procurador_propio', nombre: 'Procurador Propio' },
    { codigo: 'procurador_contrario', nombre: 'Procurador Contrario' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'asesor', nombre: 'Asesor' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'gestor', nombre: 'Gestor' },
    { codigo: 'intermediario', nombre: 'Intermediario' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'testigo_propio', nombre: 'Testigo Propio' },
    { codigo: 'testigo_contrario', nombre: 'Testigo Parte Contraria' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  empresa: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'sociedad_limitada', nombre: 'Sociedad Limitada' },
    { codigo: 'sociedad_anonima', nombre: 'Sociedad Anónima' },
    { codigo: 'comunidad_bienes', nombre: 'Comunidad de Bienes' },
    { codigo: 'sociedad_civil', nombre: 'Sociedad Civil' },
    { codigo: 'cooperativa', nombre: 'Cooperativa' },
    { codigo: 'asesoria', nombre: 'Asesoría' },
    { codigo: 'gestoria', nombre: 'Gestoría' },
    { codigo: 'aseguradora', nombre: 'Aseguradora' },
    { codigo: 'inmobiliaria', nombre: 'Inmobiliaria' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  organismo: [
    { codigo: 'juzgado', nombre: 'Juzgado' },
    { codigo: 'organismo', nombre: 'Organismo' },
    { codigo: 'notaria', nombre: 'Notaría' },
    { codigo: 'registro', nombre: 'Registro' },
    { codigo: 'administracion_publica', nombre: 'Administración Pública' },
    { codigo: 'diputacion', nombre: 'Diputación' },
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ]
};

// Lista plana de todos los subtipos únicos
const TODOS_LOS_SUBTIPOS = [
  ...SUBTIPOS_POR_ENTIDAD.persona,
  ...SUBTIPOS_POR_ENTIDAD.empresa.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo)),
  ...SUBTIPOS_POR_ENTIDAD.organismo.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo) && !SUBTIPOS_POR_ENTIDAD.empresa.find(e => e.codigo === s.codigo))
];

// Compatibilidad con código existente
const tipos = TODOS_LOS_SUBTIPOS.map(s => ({ value: s.codigo, label: s.nombre }));

// Componente de sección colapsable
const Section = ({ title, icon: Icon, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-4 bg-blue-800 text-white hover:bg-blue-900 transition-colors">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            <span className="font-medium">{title}</span>
            {badge !== null && (
              <Badge className="bg-white text-blue-800 ml-2">{badge}</Badge>
            )}
          </div>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border border-t-0 border-gray-200 bg-white p-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Componente de tabla vacía
const EmptyTable = ({ columns, message = "No hay datos" }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 border-b">
        <tr>
          {columns.map((col, idx) => (
            <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
            {message}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const ContactoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contacto, setContacto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [expedientes, setExpedientes] = useState([]);
  const [expedientesComoJuzgado, setExpedientesComoJuzgado] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [estafasBancarias, setEstafasBancarias] = useState([]);
  const [reclamacionesExtrajudiciales, setReclamacionesExtrajudiciales] = useState([]);
  
  // Estados para múltiples valores
  const [telefonos, setTelefonos] = useState([]);
  const [emails, setEmails] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [editingField, setEditingField] = useState(null);
  
  // Portal del cliente
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalExpedientes, setPortalExpedientes] = useState([]);
  const [allExpedientes, setAllExpedientes] = useState([]);

  useEffect(() => {
    loadContacto();
  }, [id]);

  const loadContacto = async () => {
    try {
      const [contactoRes, expRes, factRes] = await Promise.all([
        contactosAPI.getById(id),
        contactosAPI.getExpedientes(id),
        contactosAPI.getFacturas(id)
      ]);
      
      const data = contactoRes.data;
      setContacto(data);
      setExpedientes(expRes.data || []);
      setFacturas(factRes.data || []);
      
      // Cargar tareas de forma separada para que no rompa si falla
      try {
        const tareasRes = await contactosAPI.getTareas(id);
        setTareas(tareasRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar las tareas:', e);
        setTareas([]);
      }
      
      // Cargar estafas bancarias
      try {
        const estafasRes = await contactosAPI.getEstafasBancarias(id);
        setEstafasBancarias(estafasRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar las estafas bancarias:', e);
        setEstafasBancarias([]);
      }
      
      // Cargar reclamaciones extrajudiciales
      try {
        const reclamacionesRes = await contactosAPI.getReclamacionesExtrajudiciales(id);
        setReclamacionesExtrajudiciales(reclamacionesRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar las reclamaciones extrajudiciales:', e);
        setReclamacionesExtrajudiciales([]);
      }
      
      // Si es organismo, cargar expedientes donde actúa como juzgado
      if (data.tipo === 'organismo') {
        try {
          const expJuzgadoRes = await expedientesAPI.getByJuzgado(id);
          setExpedientesComoJuzgado(expJuzgadoRes.data || []);
        } catch (err) {
          console.log('No hay expedientes como juzgado');
          setExpedientesComoJuzgado([]);
        }
      }
      
      // Parsear datos múltiples (si vienen como JSON o crear estructura)
      setTelefonos(data.telefonos || [{ tipo: 'Móvil', numero: data.telefono || '' }]);
      setEmails(data.emails_list || [{ tipo: 'Personal', email: data.email || '' }]);
      setDirecciones(data.direcciones || [{
        tipo: 'Facturación',
        direccion: data.direccion || '',
        ciudad: data.ciudad || '',
        codigo_postal: data.codigo_postal || '',
        provincia: data.provincia || ''
      }]);
      
    } catch (error) {
      console.error('Error cargando contacto:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el contacto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContacto = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const data = {
        nombre: formData.get('nombre'),
        primer_apellido: formData.get('primer_apellido'),
        segundo_apellido: formData.get('segundo_apellido'),
        tipo: formData.get('tipo') || contacto.tipo,
        nif_cif: formData.get('nif_cif'),
        email: emails[0]?.email || formData.get('email'),
        telefono: telefonos[0]?.numero || formData.get('telefono'),
        direccion: direcciones[0]?.direccion || formData.get('direccion'),
        ciudad: direcciones[0]?.ciudad || formData.get('ciudad'),
        codigo_postal: direcciones[0]?.codigo_postal || formData.get('codigo_postal'),
        provincia: direcciones[0]?.provincia || formData.get('provincia'),
        notas: formData.get('notas')
      };

      await contactosAPI.update(id, data);
      toast({
        title: "Contacto actualizado",
        description: "Los datos se han guardado correctamente"
      });
      setIsEditDialogOpen(false);
      loadContacto();
    } catch (error) {
      console.error('Error actualizando contacto:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el contacto",
        variant: "destructive"
      });
    }
  };

  const addTelefono = () => {
    setTelefonos([...telefonos, { tipo: 'Otro', numero: '' }]);
  };

  const addEmail = () => {
    setEmails([...emails, { tipo: 'Otro', email: '' }]);
  };

  const addDireccion = () => {
    setDirecciones([...direcciones, { tipo: 'Otro', direccion: '', ciudad: '', codigo_postal: '', provincia: '' }]);
  };

  const removeTelefono = (index) => {
    setTelefonos(telefonos.filter((_, i) => i !== index));
  };

  const removeEmail = (index) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const removeDireccion = (index) => {
    setDirecciones(direcciones.filter((_, i) => i !== index));
  };

  // Funciones para Portal del Cliente
  const handleOpenPortalDialog = async () => {
    setPortalEmail(contacto.email || '');
    setPortalExpedientes([]);
    try {
      const expRes = await expedientesAPI.getAll();
      setAllExpedientes(expRes.data || []);
    } catch (err) {
      console.error('Error cargando expedientes:', err);
    }
    setShowPortalDialog(true);
  };

  const handleDarAccesoPortal = async () => {
    if (!portalEmail) {
      toast({ title: 'Error', description: 'Ingrese un email', variant: 'destructive' });
      return;
    }
    
    setPortalLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal-cliente/registrar-con-envio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacto_id: parseInt(id),
          email: portalEmail,
          expedientes_ids: portalExpedientes.map(e => parseInt(e))
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: 'Acceso creado',
          description: data.email_enviado 
            ? `Código enviado a ${portalEmail}` 
            : `Registrado. Código: ${data.codigo_acceso} (Configure email para envío automático)`
        });
        setShowPortalDialog(false);
      } else {
        throw new Error(data.detail || 'Error al crear acceso');
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'No se pudo crear el acceso', 
        variant: 'destructive' 
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      cliente: 'bg-blue-100 text-blue-700',
      contraparte: 'bg-red-100 text-red-700',
      procurador: 'bg-purple-100 text-purple-700',
      perito: 'bg-green-100 text-green-700',
      otro: 'bg-gray-100 text-gray-700'
    };
    return colors[tipo] || colors.otro;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contacto) {
    return (
      <div className="p-6">
        <p className="text-center text-gray-500">Contacto no encontrado</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/contactos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Contacto | {[contacto.nombre, contacto.primer_apellido, contacto.segundo_apellido].filter(Boolean).join(' ') || contacto.razon_social}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {contacto.tipo !== 'organismo' && (
            <Button 
              onClick={handleOpenPortalDialog} 
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Shield className="w-4 h-4 mr-2" />
              Dar acceso al portal
            </Button>
          )}
          <Button onClick={() => setIsEditDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda - Datos del contacto */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-6">
              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mb-4">
                  <User className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {[contacto.nombre, contacto.primer_apellido, contacto.segundo_apellido].filter(Boolean).join(' ') || contacto.razon_social || 'Sin nombre'}
                </h2>
                <p className="text-gray-500">
                  {contacto.tipo_entidad || 'Persona'} - <Badge className={getTipoBadge(contacto.tipo)}>
                    {tipos.find(t => t.value === contacto.tipo)?.label || contacto.tipo}
                  </Badge>
                </p>
              </div>

              {/* Datos de contacto */}
              <div className="space-y-4">
                {/* NIF/CIF */}
                {contacto.nif_cif && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">NIF/CIF</p>
                      <p className="font-medium">{contacto.nif_cif}</p>
                    </div>
                  </div>
                )}

                {/* Teléfonos */}
                {telefonos.map((tel, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{tel.tipo}</p>
                      {tel.numero ? (
                        <a href={`tel:${tel.numero}`} className="font-medium text-blue-600 hover:underline">
                          {tel.numero}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Emails */}
                {emails.map((em, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{em.tipo}</p>
                      {em.email ? (
                        <a href={`mailto:${em.email}`} className="font-medium text-blue-600 hover:underline break-all">
                          {em.email}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Direcciones */}
                {direcciones.map((dir, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{dir.tipo}</p>
                      <p className="font-medium">
                        {[dir.direccion, dir.codigo_postal, dir.ciudad, dir.provincia]
                          .filter(Boolean).join(', ') || '-'}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Notas */}
                {contacto.notas && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Notas</p>
                      <p className="font-medium">{contacto.notas}</p>
                    </div>
                  </div>
                )}

                {/* Fecha de creación */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Fecha de creación: {new Date(contacto.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha - Secciones */}
        <div className="lg:col-span-2 space-y-0">
          {/* Eventos */}
          <Section title="Eventos" icon={Calendar} defaultOpen={true} badge={eventos.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Evento
              </Button>
            </div>
            <EmptyTable columns={['Tipo', 'Título', 'Fecha de comienzo', 'Fecha de fin', 'Calendario', 'Estado', 'Acciones']} />
          </Section>

          {/* Adjuntos */}
          <Section title="Adjuntos" icon={Paperclip} badge={0}>
            <AdjuntosSection 
              entityType="contacto" 
              entityId={parseInt(id)} 
              showHeader={false}
            />
          </Section>

          {/* Tareas pendientes */}
          <Section title="Tareas pendientes" icon={CheckSquare} badge={tareas.length}>
            {tareas.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay tareas pendientes con este contacto</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asignado a</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha límite</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tareas.map((tarea, index) => (
                      <tr key={`${tarea.tipo}-${tarea.id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{tarea.titulo}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={tarea.tipo === 'atencion_cliente' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {tarea.tipo === 'atencion_cliente' ? 'Atención cliente' : 'Correo'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            tarea.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                            tarea.estado === 'en_progreso' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {tarea.estado?.replace(/_/g, ' ') || 'Pendiente'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            tarea.prioridad === 'alta' ? 'bg-red-100 text-red-700' :
                            tarea.prioridad === 'media' ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {tarea.prioridad || 'Media'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{tarea.asignado_a || '-'}</td>
                        <td className="px-4 py-3">
                          {tarea.fecha_limite ? (
                            <div className="flex items-center gap-1 text-gray-600">
                              <Clock className="w-4 h-4" />
                              {new Date(tarea.fecha_limite).toLocaleDateString('es-ES')}
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Expedientes legales */}
          <Section title="Expedientes legales" icon={Folder} badge={expedientes.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Ver archivados</Button>
                <Button variant="outline" size="sm">Exportar</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo
                </Button>
              </div>
            </div>
            {expedientes.length === 0 ? (
              <EmptyTable columns={['Serie', 'Código', 'Procedimiento', 'Rol', 'Fecha', 'Descripción', 'Acciones']} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {expedientes.map((exp) => (
                      <tr key={exp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                            {exp.numero_expediente}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{exp.titulo}</td>
                        <td className="px-4 py-3">
                          <Badge className="bg-blue-100 text-blue-700 capitalize">
                            {exp.rol?.replace(/_/g, ' ') || 'Cliente'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={exp.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {exp.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {exp.fecha_inicio ? new Date(exp.fecha_inicio).toLocaleDateString('es-ES') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/expedientes/${exp.id}`}>Ver</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Expedientes como Juzgado (solo para organismos) */}
          {contacto?.tipo === 'organismo' && (
            <Section title="Expedientes asociados como Juzgado" icon={Building} badge={expedientesComoJuzgado.length} defaultOpen={expedientesComoJuzgado.length > 0}>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Expedientes donde este organismo está asignado como juzgado
                </p>
              </div>
              {expedientesComoJuzgado.length === 0 ? (
                <EmptyTable columns={['Código', 'Exp. Judicial', 'NIG', 'Título', 'Estado', 'Fecha', 'Acciones']} message="No hay expedientes asignados" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIG</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {expedientesComoJuzgado.map((exp) => (
                        <tr key={exp.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                              {exp.numero_expediente}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{exp.expediente_judicial || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{exp.nig || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{exp.titulo}</td>
                          <td className="px-4 py-3">
                            <Badge className={exp.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {exp.estado}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {exp.fecha_inicio ? new Date(exp.fecha_inicio).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/expedientes/${exp.id}`}>Ver</Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* Estafas Bancarias */}
          <Section title="Estafas Bancarias" icon={AlertTriangle} badge={estafasBancarias.length} defaultOpen={estafasBancarias.length > 0}>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Casos de estafas bancarias donde este contacto está involucrado
              </p>
            </div>
            {estafasBancarias.length === 0 ? (
              <EmptyTable columns={['Referencia', 'Tipo Estafa', 'Banco', 'Importe', 'Estado', 'Fecha', 'Rol', 'Acciones']} message="No hay casos de estafas bancarias" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Estafa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banco</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Importe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {estafasBancarias.map((caso) => (
                      <tr key={caso.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/estafas-bancarias/${caso.id}`} className="text-blue-600 hover:underline font-medium">
                            {caso.numero_referencia}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600 capitalize">{caso.tipo_estafa?.replace('_', ' ') || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{caso.banco_nombre || '-'}</td>
                        <td className="px-4 py-3 font-medium text-red-600">
                          {caso.cantidad_estafada ? `${caso.cantidad_estafada.toLocaleString('es-ES')}€` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            caso.estado === 'expediente_creado' ? 'bg-green-100 text-green-700' :
                            caso.estado === 'inicio' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {caso.estado?.replace('_', ' ') || 'inicio'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {caso.fecha_fraude ? new Date(caso.fecha_fraude).toLocaleDateString('es-ES') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={caso.rol === 'cliente' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>
                            {caso.rol === 'cliente' ? 'Cliente/Víctima' : 'Entidad Bancaria'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/estafas-bancarias/${caso.id}`}>Ver</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Reclamaciones Extrajudiciales */}
          <Section title="Reclamaciones Extrajudiciales" icon={Scale} badge={reclamacionesExtrajudiciales.length} defaultOpen={reclamacionesExtrajudiciales.length > 0}>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Reclamaciones extrajudiciales (gastos hipotecarios, cláusulas abusivas, etc.) de este contacto
              </p>
            </div>
            {reclamacionesExtrajudiciales.length === 0 ? (
              <EmptyTable columns={['Referencia', 'Entidad Bancaria', 'Cláusulas', 'Estado', 'Fecha Hipoteca', 'Acciones']} message="No hay reclamaciones extrajudiciales" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entidad Bancaria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cláusulas</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Hipoteca</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reclamacionesExtrajudiciales.map((rec) => {
                      // Contar cláusulas activas
                      const clausulasActivas = [];
                      if (rec.clausulas?.gastos) clausulasActivas.push('Gastos');
                      if (rec.clausulas?.comision_apertura) clausulasActivas.push('Com. Apertura');
                      if (rec.clausulas?.suelo) clausulasActivas.push('Suelo');
                      if (rec.clausulas?.redondeo) clausulasActivas.push('Redondeo');
                      if (rec.clausulas?.interes_demora) clausulasActivas.push('Int. Demora');
                      if (rec.clausulas?.cierre_irph) clausulasActivas.push('IRPH');
                      
                      return (
                        <tr key={rec.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link to={`/extrajudicial/${rec.id}`} className="text-blue-600 hover:underline font-medium">
                              {rec.numero_referencia}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{rec.entidad_bancaria_actual || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {clausulasActivas.length > 0 ? (
                                clausulasActivas.map((cl, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {cl}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              rec.estado === 'finalizado' ? 'bg-green-100 text-green-700' :
                              rec.estado === 'en_tramite' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {rec.estado?.replace('_', ' ') || 'inicio'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {rec.fecha_firma_hipoteca ? new Date(rec.fecha_firma_hipoteca).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/extrajudicial/${rec.id}`}>Ver</Link>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Notas */}
          <Section title="Notas" icon={FileText} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Exportar</Button>
                <Button variant="outline" size="sm">Borrar selección</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva nota relacionada
                </Button>
              </div>
            </div>
            <EmptyTable columns={['Fecha', 'Título', 'Contenido', 'Contacto', 'Expediente legal', 'Acciones']} />
          </Section>

          {/* Contactos relacionados */}
          <Section title="Contactos" icon={Users} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Ver archivados</Button>
                <Button variant="outline" size="sm">Borrar selección</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo
                </Button>
              </div>
            </div>
            <EmptyTable columns={['Nombre', 'Tipo', 'Subtipo', 'CIF/NIF', 'Fecha nacimiento', 'Sitio web', 'Acciones']} />
          </Section>

          {/* Facturas */}
          <Section title="Facturas" icon={Euro} badge={facturas.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Descargar</Button>
                <Button variant="outline" size="sm">Enviar</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva
                </Button>
              </div>
            </div>
            {facturas.length === 0 ? (
              <EmptyTable columns={['Tipo', 'Nº Factura', 'Fecha', 'Concepto', 'Total', 'Estado', 'Acciones']} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Factura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {facturas.map((fac) => (
                      <tr key={fac.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-blue-600">{fac.numero_factura}</td>
                        <td className="px-4 py-3 text-gray-600">{fac.concepto}</td>
                        <td className="px-4 py-3 font-medium">{fac.total?.toLocaleString('es-ES')}€</td>
                        <td className="px-4 py-3">
                          <Badge className={fac.estado === 'pagada' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {fac.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {fac.fecha_emision ? new Date(fac.fecha_emision).toLocaleDateString('es-ES') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Emails */}
          <Section title="Emails" icon={Mail} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
            </div>
            <EmptyTable columns={['Fecha', 'De', 'Asunto', 'Para', 'Acciones']} />
          </Section>

          {/* Conceptos facturables */}
          <Section title="Conceptos facturables" icon={Euro} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" />
              </div>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo
              </Button>
            </div>
            <EmptyTable columns={['Fecha', 'Título', 'Cliente', 'Expediente', 'Facturado', 'Nº Factura', 'Coste total', 'Acciones']} />
          </Section>
        </div>
      </div>

      {/* Dialog de edición */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Contacto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateContacto} className="space-y-6">
            {/* Datos básicos */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre">Nombre *</Label>
                <Input id="nombre" name="nombre" defaultValue={contacto.nombre} required />
              </div>
              <div>
                <Label htmlFor="primer_apellido">Primer apellido</Label>
                <Input id="primer_apellido" name="primer_apellido" defaultValue={contacto.primer_apellido} />
              </div>
              <div>
                <Label htmlFor="segundo_apellido">Segundo apellido</Label>
                <Input id="segundo_apellido" name="segundo_apellido" defaultValue={contacto.segundo_apellido} />
              </div>
              <div>
                <Label htmlFor="tipo">Tipo de contacto</Label>
                <Select name="tipo" defaultValue={contacto.tipo}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipos.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="nif_cif">NIF/CIF</Label>
                <Input id="nif_cif" name="nif_cif" defaultValue={contacto.nif_cif} />
              </div>
            </div>

            {/* Teléfonos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Teléfonos</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTelefono}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir
                </Button>
              </div>
              <div className="space-y-2">
                {telefonos.map((tel, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Select 
                      value={tel.tipo} 
                      onValueChange={(v) => {
                        const newTels = [...telefonos];
                        newTels[idx].tipo = v;
                        setTelefonos(newTels);
                      }}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Móvil">Móvil</SelectItem>
                        <SelectItem value="Trabajo">Trabajo</SelectItem>
                        <SelectItem value="Casa">Casa</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      value={tel.numero} 
                      onChange={(e) => {
                        const newTels = [...telefonos];
                        newTels[idx].numero = e.target.value;
                        setTelefonos(newTels);
                      }}
                      placeholder="Número de teléfono"
                      className="flex-1"
                    />
                    {telefonos.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTelefono(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Emails */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Emails</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEmail}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir
                </Button>
              </div>
              <div className="space-y-2">
                {emails.map((em, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Select 
                      value={em.tipo} 
                      onValueChange={(v) => {
                        const newEmails = [...emails];
                        newEmails[idx].tipo = v;
                        setEmails(newEmails);
                      }}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Personal">Personal</SelectItem>
                        <SelectItem value="Trabajo">Trabajo</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      type="email"
                      value={em.email} 
                      onChange={(e) => {
                        const newEmails = [...emails];
                        newEmails[idx].email = e.target.value;
                        setEmails(newEmails);
                      }}
                      placeholder="Correo electrónico"
                      className="flex-1"
                    />
                    {emails.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeEmail(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Direcciones */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Direcciones</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDireccion}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir
                </Button>
              </div>
              <div className="space-y-4">
                {direcciones.map((dir, idx) => (
                  <div key={idx} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Select 
                        value={dir.tipo} 
                        onValueChange={(v) => {
                          const newDirs = [...direcciones];
                          newDirs[idx].tipo = v;
                          setDirecciones(newDirs);
                        }}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Facturación">Facturación</SelectItem>
                          <SelectItem value="Envío">Envío</SelectItem>
                          <SelectItem value="Personal">Personal</SelectItem>
                          <SelectItem value="Trabajo">Trabajo</SelectItem>
                          <SelectItem value="Otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                      {direcciones.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeDireccion(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    <Input 
                      value={dir.direccion} 
                      onChange={(e) => {
                        const newDirs = [...direcciones];
                        newDirs[idx].direccion = e.target.value;
                        setDirecciones(newDirs);
                      }}
                      placeholder="Dirección"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input 
                        value={dir.codigo_postal} 
                        onChange={(e) => {
                          const newDirs = [...direcciones];
                          newDirs[idx].codigo_postal = e.target.value;
                          setDirecciones(newDirs);
                        }}
                        placeholder="C.P."
                      />
                      <Input 
                        value={dir.ciudad} 
                        onChange={(e) => {
                          const newDirs = [...direcciones];
                          newDirs[idx].ciudad = e.target.value;
                          setDirecciones(newDirs);
                        }}
                        placeholder="Ciudad"
                      />
                      <Input 
                        value={dir.provincia} 
                        onChange={(e) => {
                          const newDirs = [...direcciones];
                          newDirs[idx].provincia = e.target.value;
                          setDirecciones(newDirs);
                        }}
                        placeholder="Provincia"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notas */}
            <div>
              <Label htmlFor="notas">Notas</Label>
              <Textarea id="notas" name="notas" rows={3} defaultValue={contacto.notas} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar cambios</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Dar acceso al Portal del Cliente */}
      <Dialog open={showPortalDialog} onOpenChange={setShowPortalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Dar acceso al Portal del Cliente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Al dar acceso, el cliente podrá consultar el estado de sus expedientes y enviar consultas.
              Se generará un código de acceso automáticamente y se enviará por email.
            </p>
            
            <div>
              <Label>Email de acceso *</Label>
              <Input 
                type="email"
                value={portalEmail}
                onChange={(e) => setPortalEmail(e.target.value)}
                placeholder="email@ejemplo.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Expedientes a los que tendrá acceso</Label>
              <div className="border rounded-lg max-h-[200px] overflow-y-auto mt-1">
                {allExpedientes.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 text-center">No hay expedientes</p>
                ) : (
                  allExpedientes.map(exp => (
                    <label 
                      key={exp.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input 
                        type="checkbox"
                        checked={portalExpedientes.includes(exp.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPortalExpedientes([...portalExpedientes, exp.id]);
                          } else {
                            setPortalExpedientes(portalExpedientes.filter(id => id !== exp.id));
                          }
                        }}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div>
                        <p className="font-medium text-sm">{exp.numero_expediente}</p>
                        <p className="text-xs text-gray-500">{exp.titulo}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPortalDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDarAccesoPortal}
              disabled={portalLoading || !portalEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Crear acceso y enviar código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactoDetalle;
